/**
 * OutcomesExportButton — "Export" control for the Outcomes report.
 *
 * Kicks off a PDF export of the current framework + filters and shows a small
 * progress dialog through to a download. Self-contained (shared shadcn ui only,
 * no cause-report imports) so retiring the Cause report UI can't break it.
 */

import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Download, FileDown, Loader2, XCircle } from 'lucide-react';

import { Button } from '../../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../ui/dialog';
import { Progress } from '../../../../ui/progress';
import { cn } from '../../../../../lib/utils';
import { downloadFileWithAuth } from '../../../../../utils/api';
import type { FilterConfig } from '../../../records/filter';
import type { Framework } from '../types';
import { useOutcomesReportExport } from '../hooks/useOutcomesReportExport';

export function OutcomesExportButton({
  datasetId,
  framework,
  filters,
  disabled,
  className,
}: {
  datasetId: string;
  framework: Framework;
  filters: FilterConfig;
  disabled?: boolean;
  className?: string;
}) {
  const [showModal, setShowModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { exportReport, status, progress, downloadUrl, error, reset, isExporting, resumed } =
    useOutcomesReportExport(datasetId, framework);

  useEffect(
    () => () => {
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    },
    [],
  );

  // A rehydrated export (restored on mount after an unmount/refresh) has no click
  // to open the dialog — open it here so the user can see progress and reach the
  // download, which the disabled/re-triggering button otherwise can't surface.
  useEffect(() => {
    if (resumed) setShowModal(true);
  }, [resumed]);

  const handleExport = () => {
    // Cancel a pending post-close reset so reopening quickly can't wipe the
    // state of the export we're about to start.
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
    setDownloadError(null);
    setShowModal(true);
    exportReport({ datasetId, framework, filters });
  };

  const handleClose = () => {
    setShowModal(false);
    // Reset after the close animation so the dialog doesn't flicker to idle.
    resetTimeoutRef.current = setTimeout(reset, 300);
  };

  const handleDownload = async () => {
    if (!downloadUrl) return;
    setIsDownloading(true);
    setDownloadError(null);
    try {
      await downloadFileWithAuth(downloadUrl, 'outcomes-report.pdf');
    } catch (err) {
      console.error('Download failed:', err);
      setDownloadError('Failed to download. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        disabled={disabled || isExporting}
        className={cn('rounded-full border-border/70 bg-background/90 shadow-sm', className)}
      >
        {isExporting ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <FileDown className="mr-2 h-4 w-4" />
        )}
        Export
      </Button>

      <Dialog
        open={showModal}
        onOpenChange={(open) => {
          // Don't let X / Escape / outside-click dismiss while a job is running —
          // dismissing would cancel polling and orphan a backend export that
          // then 429s the next attempt. Closing is allowed once ready/errored.
          if (!open && !isExporting) handleClose();
        }}
      >
        <DialogContent className="sm:max-w-md rounded-[24px] border-border/70 bg-background/95 shadow-[var(--shadow-card)] backdrop-blur-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="h-5 w-5" />
              Export Outcomes Report
            </DialogTitle>
            <DialogDescription>
              {status === 'generating' && 'Generating your PDF report…'}
              {status === 'ready' && 'Your report is ready for download.'}
              {status === 'error' && 'There was a problem generating your report.'}
              {status === 'idle' && 'Preparing your export…'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center justify-center space-y-4 py-6">
            {(status === 'generating' || status === 'idle') && (
              <>
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Generating report… {progress ?? 0}%</p>
                <Progress value={progress} className="w-full" />
              </>
            )}

            {status === 'ready' && (
              <>
                <div className="rounded-full bg-primary/10 p-3">
                  <CheckCircle2 className="h-12 w-12 text-primary" />
                </div>
                <p className="text-center text-sm text-muted-foreground">
                  Your Outcomes Report PDF has been generated successfully.
                </p>
                <Button onClick={handleDownload} className="w-full" disabled={isDownloading}>
                  {isDownloading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Downloading…
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </>
                  )}
                </Button>
                {downloadError && (
                  <p className="text-center text-sm text-red-600">{downloadError}</p>
                )}
              </>
            )}

            {status === 'error' && (
              <>
                <div className="rounded-full bg-destructive/10 p-3">
                  <XCircle className="h-12 w-12 text-destructive" />
                </div>
                <p className="line-clamp-3 max-w-full break-words text-center text-sm text-destructive">
                  {error || 'An unexpected error occurred.'}
                </p>
                <Button variant="outline" onClick={handleClose}>
                  Close
                </Button>
              </>
            )}
          </div>

          {status === 'ready' && (
            <div className="flex justify-end">
              <Button variant="ghost" onClick={handleClose}>
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
